import Vue from "vue";
import App from "./App.vue";
import DashboardPlugin from "./plugins/dashboard-plugin";
import vuetify from "./plugins/vuetify";
import router from "./router";

import CampoCompetencia from "@/components/base/proprios/CampoCompetencia";
import CampoData from "@/components/base/proprios/CampoData";
import CampoDataHora from "@/components/base/proprios/CampoDataHora";
import ConfirmaAcao from "@/components/base/proprios/ConfirmaAcao";
import { formValidationRules } from "@/mixins/formValidationRules";
import Axios from "axios";
import "sweetalert2/dist/sweetalert2.min.css";

import { initMessagingListeners } from "@/services/push/firebase";
import { applyFontSizePreference } from "@/utils/fontSizePreference";
import { syncDocumentLocale } from "@/utils/locale";
import { applyWhiteLabelDocumentBrand } from "@/utils/whiteLabel";
import VueMask, { VueMaskDirective, VueMaskFilter } from "v-mask";
import VueSweetAlert2 from "vue-sweetalert2";
import VuetifyMask from "vuetify-mask";
import ConfirmButton from "./components/base/ConfirmButton";
import ErrorAlert from "./components/base/ErrorAlert";
import ReturnDetail from "./components/base/ReturnDetail";
import ConfirmationOverlay from "./components/base/proprios/ConfirmationOverlay";
import LoadingOverlay from "./components/base/proprios/LoadingOverlay";
import CampoCfopCidade from "./components/base/proprios/campos/CampoCfopCidade";
import CampoCfopEstado from "./components/base/proprios/campos/CampoCfopEstado";
import ChartBar from "./components/base/proprios/charts/ChartBar";
import Doughnut from "./components/base/proprios/charts/Doughnut";
import i18n from "./i18n";
import { checkPendingTutorialModal } from "./mixins/checkPendingTutorialModal";
import limpaMascara from "./mixins/limpaMascara";
import utils from "./mixins/utils";
import "./plugins/base";
import "./plugins/chartist";
import TutorialRunnerPlugin from "./plugins/tutorial-runner";
import "./plugins/vee-validate";
import store from "./store";

Vue.config.productionTip = false;

// Photoswipe Gallery
import Photoswipe from "vue-pswipe";
Vue.use(Photoswipe);

// plugin setup
Vue.use(DashboardPlugin);
Vue.use(TutorialRunnerPlugin, { router });

const CaixaLegenda = () => import("./components/base/CaixaLegenda");
Vue.component("CaixaLegenda", CaixaLegenda);

Vue.use(VuetifyMask);
Vue.use(VueSweetAlert2);

function liberarFocoAtivoAntesDeOverlay() {
  if (typeof document === "undefined") return;

  const activeElement = document.activeElement;
  if (
    !activeElement ||
    activeElement === document.body ||
    typeof activeElement.blur !== "function"
  ) {
    return;
  }

  activeElement.blur();
}

function wrapSwalFocusRelease(swal) {
  if (!swal || swal.__cajupeFocusReleaseWrapped) return swal;

  const wrappedSwal = function (...args) {
    liberarFocoAtivoAntesDeOverlay();
    return swal.apply(this, args);
  };

  Object.assign(wrappedSwal, swal);

  Object.keys(swal).forEach((key) => {
    const value = swal[key];
    if (typeof value !== "function") {
      wrappedSwal[key] = value;
      return;
    }

    wrappedSwal[key] = function (...args) {
      if (key === "fire") {
        liberarFocoAtivoAntesDeOverlay();
      }
      return value.apply(swal, args);
    };
  });

  wrappedSwal.fire = function (...args) {
    liberarFocoAtivoAntesDeOverlay();
    return swal.fire.apply(swal, args);
  };
  wrappedSwal.__cajupeFocusReleaseWrapped = true;

  return wrappedSwal;
}

Vue.prototype.$swal = wrapSwalFocusRelease(Vue.prototype.$swal);
Vue.swal = Vue.prototype.$swal;

Vue.component("LoadingOverlay", LoadingOverlay);
Vue.component("ChartBar", ChartBar);
Vue.component("Doughnut", Doughnut);
Vue.component("ConfirmationOverlay", ConfirmationOverlay);
Vue.component("ConfirmButton", ConfirmButton);
Vue.component("CampoCfopEstado", CampoCfopEstado);
Vue.component("CampoCfopCidade", CampoCfopCidade);
Vue.component("CampoData", CampoData);
Vue.component("CampoCompetencia", CampoCompetencia);
Vue.component("CampoDataHora", CampoDataHora);
Vue.component("ErrorAlert", ErrorAlert);
Vue.component("ReturnDetail", ReturnDetail);
Vue.component("ConfirmaAcao", ConfirmaAcao);

Vue.filter("dataParaLeitura", function (value) {
  if (!value) return;
  return dataHoraParaLeitura(value);
});
Vue.filter("competenciaParaLeitura", function (value) {
  if (!value) return;
  return competenciaParaLeitura(value);
});
Vue.filter("horaParaLeitura", function (value) {
  if (!value) return;
  return horaParaLeitura(value);
});
Vue.filter("dataHoraParaLeituraComSegundo", function (value) {
  if (!value) return;
  return dataHoraParaLeituraComSegundo(value);
});

function dataHoraParaLeitura(value) {
  if (!value) return;
  if (value.length === 8) return value.substr(0, 5);
  if (value.length >= 10) {
    let stringData =
      value.substr(8, 2) + "/" + value.substr(5, 2) + "/" + value.substr(0, 4);
    let stringHora =
      value.length >= 16 ? value.substr(11, 2) + ":" + value.substr(14, 2) : "";
    return stringHora ? stringData + " " + stringHora : stringData;
  }
  return value;
}
function dataHoraParaLeituraComSegundo(value) {
  if (!value) return;
  let stringData =
    value.substr(8, 2) + "/" + value.substr(5, 2) + "/" + value.substr(0, 4);
  let stringHora =
    value.substr(11, 2) + ":" + value.substr(14, 2) + ":" + value.substr(17, 2);
  return value.length > 10 ? stringData + " " + stringHora : stringData;
}
function horaParaLeitura(value) {
  if (!value) return;
  if (value.length > 10) return value.substr(11, 2) + ":" + value.substr(14, 2);
  return value.substr(0, 2) + ":" + value.substr(3, 2);
}
function competenciaParaLeitura(value) {
  if (!value) return;
  let stringData = value.substr(5, 2) + "/" + value.substr(0, 4);
  return stringData;
}

Vue.filter("dataHoraParaLeitura", function (value) {
  if (!value) return;
  return dataHoraParaLeitura(value);
});
Vue.filter("numeroTresCasasDecimais", function (value) {
  let zero = (0).toLocaleString("pt-br");
  if (!value) return zero;
  let valueNumber = Number(value);
  return valueNumber.toLocaleString("pt-br");
});
Vue.filter("formatoNumero", function (value) {
  let zero = (0).toLocaleString("pt-br");
  if (!value) return zero;
  let valueNumber = Number(value);
  return valueNumber.toLocaleString("pt-br");
});
Vue.filter("formatoMonetario", function (value) {
  let zero = (0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });
  if (!value) return zero;
  let valueNumber = Number(value);
  return valueNumber.toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });
});
Vue.filter("formatoPercentual", function (value) {
  let valueNumber = Number(value);
  if (value === undefined) valueNumber = 0;
  valueNumber.toFixed(2);
  return value + "%";
});
// Percentual no formato pt-BR, com até "casas" decimais (no mínimo duas)
Vue.filter("formatoPercentualExato", function (value, casas = 4) {
  if (value === undefined || value === null || value === "") return "";
  const valueNumber = Number(value);
  if (Number.isNaN(valueNumber)) return value + "%";
  return (
    valueNumber.toLocaleString("pt-BR", {
      minimumFractionDigits: Math.min(2, casas),
      maximumFractionDigits: casas,
    }) + "%"
  );
});
Vue.filter("capitalize", function (value) {
  return value
    ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    : "";
});
Vue.filter("formatoNumeroDecimal", function (value) {
  if (!value) return "0,00";
  let valueNumber = Number(value);
  return valueNumber.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
});
Vue.filter("formatoNumeroInteiro", function (value) {
  if (!value) return "0";
  let valueNumber = Number(value);
  return valueNumber.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
});

Vue.use(VueMask);
Vue.directive("mask", VueMaskDirective);
Vue.filter("VMask", VueMaskFilter);

//Configurações Axios
const baseURL = "https://appcajupe.com:8443/";
//const baseURL = "http://localhost:8080/";
Axios.defaults.baseURL = baseURL;
Axios.defaults.timeout = 1800000;
window.baseApiURL = baseURL;

// Hidrata a Store a partir do localStorage (sem nova chamada à API)
store.dispatch("HYDRATE_FROM_LOCAL");
applyFontSizePreference(store.state.user?.tamanhoFonte);
store.watch(
  (state) => state.user?.tamanhoFonte,
  (tamanhoFonte) => applyFontSizePreference(tamanhoFonte)
);

//Diretiva de permissão (usa array de strings do Vuex)
Vue.directive("can", {
  bind: function (el, binding, vnode) {
    if (!binding.value || !store.state.permissoes) return;
    if (
      !store.state.permissoes.includes(
        String(binding.value).replace(/'/g, "").replace(/"/g, "")
      )
    ) {
      vnode.elm.style.display = "none";
    }
  },
});

Vue.mixin(checkPendingTutorialModal);
Vue.mixin(limpaMascara);
Vue.mixin(formValidationRules);
Vue.mixin(utils);

initMessagingListeners(store);

applyWhiteLabelDocumentBrand(undefined, i18n);
router.afterEach(() => {
  applyWhiteLabelDocumentBrand(undefined, i18n);
});

new Vue({
  router,
  vuetify,
  store,
  Axios,
  i18n,
  data() {
    return { expression: "" };
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler(locale) {
        const normalizedLocale = syncDocumentLocale(locale);

        if (this.$vuetify?.lang) {
          this.$vuetify.lang.current = normalizedLocale;
        }

        applyWhiteLabelDocumentBrand(undefined, this.$i18n);
      },
    },
  },
  render: (h) => h(App),
}).$mount("#app");
